import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GitMerge } from 'lucide-react';
import { Link } from 'react-router';
import type { TicketDetail } from '@aurora/contracts';
import { post, type ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useToast } from '../../components/toast';
import { Button } from '../../components/ui';
import { refreshTicket } from './detail-data';

/**
 * The pointer a merged ticket keeps to where its conversation went.
 *
 * A merged ticket used to open as a closed ticket with its replies gone and
 * nothing to say where — the only trace was a "merged_into" event that read
 * "<agent> changed". Now it says, links there, and (for whoever may merge)
 * offers to undo it. It takes no new messages: the server refuses them and
 * points to the ticket that is being worked.
 */
export function MergedBanner({ ticket }: { ticket: TicketDetail }) {
  const queryClient = useQueryClient();
  const { session, can } = useSession();
  const toast = useToast();
  const isAgent = Boolean(session?.is_agent);
  const target = ticket.merged_into?.number;

  const undo = useMutation<{ data: { target?: number } }, ApiError, void>({
    mutationFn: () => post(`/tickets/${ticket.number}/unmerge`),
    onSuccess: (response) => {
      toast.success(`Undid the merge of #${ticket.number}`, 'Its messages are back on this ticket.');
      refreshTicket(queryClient, String(ticket.number), ['watchers', 'links']);
      const into = response.data?.target ?? target;
      if (into) refreshTicket(queryClient, String(into), ['watchers', 'links']);
    },
    onError: (error) => toast.error(error),
  });

  if (!target) return null;

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-informative-line bg-informative-soft px-4 py-2 sm:px-6"
    >
      <GitMerge aria-hidden className="size-4 shrink-0 text-informative-ink" />
      <p className="min-w-0 flex-1 text-body text-informative-ink">
        {isAgent ? (
          <>
            This ticket was merged into{' '}
            <Link to={`/tickets/${target}`} className="font-medium underline">
              #{target}
            </Link>
            . Its conversation continues there.
          </>
        ) : (
          <>This request was combined with #{target} by the service desk, and the conversation continues there.</>
        )}
      </p>
      {isAgent && (
        <div className="flex items-center gap-2">
          <Button asChild size="sm">
            <Link to={`/tickets/${target}`}>Open #{target}</Link>
          </Button>
          {can('ticket.merge') && (
            <Button size="sm" variant="ghost" loading={undo.isPending} onClick={() => undo.mutate()}>
              Undo merge
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
